import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Navbar } from "./Navbar";
import { AdminBar } from "./AdminBar";
import { Footer } from "./Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { useTheme, useThemeVariant } from "@/contexts/ThemeContext";
import { useIsThemePreviewFrame } from "@/hooks/useIsThemePreviewFrame";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { prefersReducedMotion } from "@/hooks/useTenantMotion";

interface LayoutProps {
  children: ReactNode;
}

/** The branded first-load veil plays once per page load, never on in-app navigation. */
let introPlayed = false;
/** Longest we hold the veil while the tenant is still resolving. */
const INTRO_MAX_MS = 900;
/** Once the tenant is known, how long we wait for its theme (tenants without saved theme settings never get one). */
const THEME_GRACE_MS = 380;

type IntroState = "playing" | "done" | "off";

export function Layout({ children }: LayoutProps) {
  const { pathname } = useLocation();
  const isDemoRoute = pathname === "/demo" || pathname.startsWith("/demo/");
  const { isOrgOwner, staffRole } = useAuth();
  const { isMarketingSite, loading: orgLoading } = useOrg();
  const theme = useTheme();
  const variant = useThemeVariant();
  const isPreviewFrame = useIsThemePreviewFrame();
  const showAdminBar = (isOrgOwner || staffRole === 'super_admin') && !isMarketingSite && !isPreviewFrame;
  const rootRef = useRef<HTMLDivElement>(null);
  useScrollReveal(rootRef);

  // First visit: a quiet veil in the page colour covers the moment the tenant
  // and its theme resolve, then lifts and the page choreography plays — instead
  // of default colours/fonts popping into the tenant's brand.
  const [intro, setIntro] = useState<IntroState>(() => (introPlayed || isPreviewFrame || prefersReducedMotion() ? "off" : "playing"));

  useEffect(() => {
    introPlayed = true;
  }, []);

  useEffect(() => {
    if (intro !== "playing") return;
    const delay = orgLoading ? INTRO_MAX_MS : theme !== null ? 80 : THEME_GRACE_MS;
    const timer = window.setTimeout(() => setIntro("done"), delay);
    return () => window.clearTimeout(timer);
  }, [intro, orgLoading, theme]);

  return (
    <div ref={rootRef} className="tp-site min-h-screen flex flex-col bg-background" data-intro={intro} data-variant={variant}>
      {!isPreviewFrame && <AdminBar />}
      <Navbar />
      <main className={`flex-1 ${showAdminBar ? 'pt-[116px]' : 'pt-20'}`}>
        {/* Layout remounts on every route change, so this fades each page in
            while the navbar and footer stay put. */}
        <div className="tp-page">{children}</div>
      </main>
      {isDemoRoute && (
        <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center overflow-hidden" aria-hidden>
          <div className="rotate-[-18deg] select-none whitespace-nowrap font-display text-6xl uppercase text-primary/10 md:text-8xl">
            Demo Site
          </div>
        </div>
      )}
      <Footer />
      {intro !== "off" && (
        <div className="tp-intro-veil" data-state={intro} aria-hidden>
          <span className="tp-intro-line" />
        </div>
      )}
    </div>
  );
}
